import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import ProfileContainer from "./container";
import { useUpdateProfileMutation } from "./services";
import { setPOSData } from "pages/login/services";
import { useUploadImageMutation } from "pages/categories/services";

const Profile = () => {
    const dispatch = useDispatch();
    const posData = useSelector((state) => state?.posData);
    const vendorData = posData?.vendor;
    const initialValuesRef = useRef(null);

    const selectedCategoryNames = Array.isArray(vendorData?.selected_categories)
        ? vendorData.selected_categories.map((c) => c.name).join(", ")
        : "";

    const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
    const isProfileLoading = !vendorData;
    const [uploadImage] = useUploadImageMutation();

    const [showPassword, setShowPassword] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState([]);

    const initialValues = {
        name: vendorData?.name || "",
        tagline: vendorData?.tagline || "",
        email: posData?.email || "", // This is the user email typically
        restaurant_website: vendorData?.restaurant_website || "",
        description: vendorData?.description || "",
        currency: vendorData?.currency || "EURO",
        service_tax: vendorData?.service_tax || "",
        platform_fee: vendorData?.platform_fee || "",
        vat_number: vendorData?.vat_number || "",
        category_id: [],

        // Location Details
        title: vendorData?.locations?.title || "",
        address: vendorData?.locations?.address || "",
        address_line2: vendorData?.locations?.address_line2 || "",
        city: vendorData?.locations?.city || "",
        state: vendorData?.locations?.state || "",
        country: vendorData?.locations?.country || "United Kingdom",
        postal_code: vendorData?.locations?.postal_code || "",
        lat: vendorData?.locations?.lat || "",
        long: vendorData?.locations?.long || "",

        // Contact Details
        full_name: posData?.full_name || "",
        personal_email: posData?.personal_email || "",
        phone: posData?.phone || "",
        alternate_contact_number: posData?.alternate_contact_number || "",
        country_code: posData?.country_code || "44",
        country_ISO: posData?.country_ISO || "GB",

        image: vendorData?.image || null,
        banner_image: vendorData?.banner_image || null,
        imageFile: null,
        bannerImageFile: null,
    };

    if (!initialValuesRef.current) {
        initialValuesRef.current = initialValues;
    }

    const validationSchema = Yup.object({
        name: Yup.string().required("Restaurant Name is required"),
        email: Yup.string().email("Invalid email").required("Email is required"),
        full_name: Yup.string().required("Full Name is required"),
        personal_email: Yup.string().email("Invalid email").required("Personal Email is required"),
        phone: Yup.string().required("Phone number is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        country: Yup.string().required("Country is required"),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                let imageId = values.image;
                let bannerImageId = values.banner_image;

                // 1. Upload Profile Image if changed
                if (values.imageFile) {
                    const formData = new FormData();
                    formData.append("file", values.imageFile);
                    const imageResponse = await uploadImage(formData).unwrap();
                    imageId = imageResponse?.data;
                }

                // 2. Upload Banner Image if changed
                if (values.bannerImageFile) {
                    const formData = new FormData();
                    formData.append("file", values.bannerImageFile);
                    const bannerResponse = await uploadImage(formData).unwrap();
                    bannerImageId = bannerResponse?.data;
                }

                const cleanCountryCode = values?.country_code?.toString().replace(/^\+/, "");

                // 3. Prepare JSON payload
                const payload = {
                    ...values,
                    image: imageId,
                    banner_image: bannerImageId,
                    country_code: cleanCountryCode,
                    // Transform category_id if it is an array of objects into an array of ids.
                    category_id: Array.isArray(values.category_id)
                        ? values.category_id.map((cat) => (typeof cat === "object" ? cat.value : cat))
                        : [],
                    vendorId: vendorData?.id
                };

                // Remove file objects from payload
                delete payload.imageFile;
                delete payload.bannerImageFile;

                const response = await updateProfile(payload).unwrap();
                if (response.status == 200) {
                    if (response.data) {
                        dispatch(setPOSData({ ...response.data, token: posData?.token }));
                    }
                }
            } catch (error) {
                console.log(error?.data?.message || "Failed to update profile")
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleTypeChange = useCallback((e, newValue) => {
        setSelectedTypes(newValue || []);
        formik.setFieldValue("category_id", newValue || []);
    }, [formik]);

    const handleFileUpload = (field, acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            if (field === "image") {
                formik.setFieldValue("imageFile", file);
                formik.setFieldValue("image", URL.createObjectURL(file));
            } else if (field === "banner_image") {
                formik.setFieldValue("bannerImageFile", file);
                formik.setFieldValue("banner_image", URL.createObjectURL(file));
            }
        }
    };

    return (
        <>
            <ProfileContainer
                formik={formik}
                showPassword={showPassword}
                handleTogglePasswordVisibility={handleTogglePasswordVisibility}
                handleFileUpload={handleFileUpload}
                selectedType={selectedTypes}
                handleTypeChange={handleTypeChange}
                initialImage={formik.values.image}
                initialBannerImage={formik.values.banner_image}
                isLoading={isProfileLoading}
                isUpdating={isUpdating}
                selectedCategoryNames={selectedCategoryNames}
            />
        </>
    );
};

export default Profile;
